import {
  eachMonthOfInterval,
  endOfMonth,
  subQuarters,
  subDays,
  endOfYear,
} from 'date-fns';
import { InteractionType } from './interaction';
import { ListItem } from './list-item';
import { Security, SecurityAccessListItem } from './security';
import { SalesTeam } from './sales-team';
import { SystemConfiguration } from './system-configuration';
import { User } from './user';
import { ConfigurationSettingCodes } from './configuration-setting';

export interface Quota {
  [name: string]: number | string | Security | null;

  key: string | null;
  parentKey: string;
  parentName: string;
  teams: string;
  fiscalYear: number;
  quarter1: number;
  quarter2: number;
  quarter3: number;
  quarter4: number;
  month0: number;
  month1: number;
  month2: number;
  month3: number;
  month4: number;
  month5: number;
  month6: number;
  month7: number;
  month8: number;
  month9: number;
  month10: number;
  month11: number;
  total: number;
  security: Security;
}

export type QuotaType = 'revenue' | 'activity' | undefined;
export enum QuotaRevenueBy {
  user = 'user',
  team = 'team',
}
export const QuotaRevenueBySettingCodes: ConfigurationSettingCodes = {
  code3: 'Preferences',
  code4: 'RevenueTargetsBy',
};

export interface QuotaRevenue extends Quota {
  revenueType: string;
}

export interface QuotaRevenueFieldOption {
  revenueTypes: ListItem<string>[];
  readAccess: SecurityAccessListItem[];
  fullAccess: SecurityAccessListItem[];
}

export interface QuotaActivity extends Quota {
  interactionCategory: string;
  interactionType: string;
}

export interface QuotaActivityFieldOption {
  categories: ListItem<string>[];
  types: InteractionType[];
  readAccess: SecurityAccessListItem[];
  fullAccess: SecurityAccessListItem[];
}

export class QuotaMonth {
  readonly end: Date;
  readonly number: number;
  readonly year: number;
  _total = 0;

  constructor(
    public readonly start: Date,
    public readonly index: number,
  ) {
    this.end = endOfMonth(start);
    this.number = start.getMonth();
    this.year = start.getFullYear();
  }

  get total(): number {
    return this._total;
  }

  set total(value: number | undefined | null) {
    this._total = value ?? 0;
  }
}

export class QuotaQuarter {
  readonly months: QuotaMonth[];

  constructor(
    public readonly number: number,
    months: Date[],
  ) {
    const baseIndex = (number - 1) * 3;
    this.months = months.map(
      (month, index) => new QuotaMonth(month, baseIndex + index),
    );
  }

  get total(): number {
    return this.months.reduce((total, month) => {
      return total + (month.total ?? 0);
    }, 0);
  }
}

export class QuotaYear {
  readonly quarters: QuotaQuarter[];
  readonly months: QuotaMonth[];

  constructor(year: number, month: number) {
    const referenceDate = new Date(year, month, 1);
    const startsOnJanuary = month === 0;
    const end = startsOnJanuary
      ? endOfYear(referenceDate)
      : subDays(referenceDate, 1);
    const start = startsOnJanuary
      ? referenceDate
      : subQuarters(referenceDate, 4);
    const months = eachMonthOfInterval({ start, end });

    this.quarters = [
      new QuotaQuarter(1, months.slice(0, 3)),
      new QuotaQuarter(2, months.slice(3, 6)),
      new QuotaQuarter(3, months.slice(6, 9)),
      new QuotaQuarter(4, months.slice(9, 12)),
    ];

    this.months = this.quarters.reduce((yearMonths, quarter) => {
      yearMonths.push(...quarter.months);
      return yearMonths;
    }, [] as QuotaMonth[]);
  }

  get total(): number {
    return this.quarters.reduce((total, quarter) => {
      return total + quarter.total;
    }, 0);
  }

  getQuarterForMonth(month: number): QuotaQuarter | undefined {
    return this.quarters.find(
      (quarter) => quarter.months.findIndex((m) => m.number === month) > -1,
    );
  }

  applyValueBelow(monthIndex: number): void {
    const value = this.months[monthIndex].total;

    for (let index = monthIndex; index < this.months.length; index++) {
      this.months[index].total = value;
    }
  }
}

export interface QuotaForm {
  key: string | null;
  year: number;
  parent: string;
  revenueType: string;
  interactionType: string;
  interactionCategory: string;
  quota?: QuotaYear;
  fullAccess: string;
  readAccess: string;
}

export interface QuotaModuleConfiguration {
  currentUser: User;
  salesTeams: SalesTeam[];
  salesRepresentatives: User[];
  revenueOptions: QuotaRevenueFieldOption;
  activityOptions: QuotaActivityFieldOption;
  systemConfiguration: SystemConfiguration;
  revenueBy: QuotaRevenueBy;
}

export interface QuotaFilters {
  users: string[] | 'all';
  teams: string[] | 'all';
  fiscalYear: number;
  revenueTypes: string[];
  interactionTypes: string[];
  interactionCategories: string[];
}
